import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

import {
  Row,
  Col,
  message,
  Image,
  Button,
  Input,
  Switch,
  Space,
  Modal,
  Divider,
  Radio,
  Tooltip,
} from 'antd';
import { DownloadOutlined, PictureOutlined } from '@ant-design/icons';
import { fetchVehicle } from '../store/actions/vehicles-actions';
import {
  fetchVehicleImages,
  removeVehicleImage,
  editVehicleImage,
  fetchVehicleImagesBrands,
} from '../store/actions/vehicleImages-actions';
import Spinner from '../components/UI/Spinner/Spinner';
import VehicleImageItem from '../components/Vehicles/Vehicle/VehicleImageItem';
import VehicleInformation from '../components/Vehicles/Vehicle/VehicleInformation';
import { fetchTags } from '../store/actions/tags-actions';

import { purchaseVehicle } from '../lib/subscriptionsAndCredits';
import { fetchCarHouseById } from '../store/actions/carHouse-actions';

const Vehicle = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { vehicle, isLoading } = useSelector((state) => state.vehicle);
  const { tags } = useSelector((state) => state.tags);
  const [isDownloading, setIsDownloading] = useState(false);
  const { vehicleImages, isLoadingImages } = useSelector(
    (state) => state.vehicleImage
  );
  const { vehicleId } = useParams();

  const { loggedInUser } = useSelector((state) => state.auth);

  const [enabledLogo, setEnabledLogo] = useState(false);
  const [symbolPhotoText, setSymbolPhotoText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [tempSymbolPhotoText, setTempSymbolPhotoText] = useState('');
  const [textSize, setTextSize] = useState(1); // Add state for text size

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(fetchVehicleImages({ vehicleId, options: { signal } })),
          dispatch(fetchVehicle({ vehicleId, options: { signal } })),
          dispatch(fetchTags()),
        ]);
      } catch (error) {
        console.error('Failed to fetch vehicle data:', error);
      }
    };
    fetchData();

    return () => controller.abort();
  }, [dispatch, vehicleId]);

  const setAsPreviewImageHandler = async (imageId) => {
    try {
      const currentPreview = vehicleImages.find((img) => img.order === 1);
      if (currentPreview) {
        const result = await dispatch(
          editVehicleImage({
            vehicleId,
            imageId: currentPreview.id,
            body: { order: 0 },
          })
        );

        if (editVehicleImage.rejected.match(result)) {
          throw result.payload;
        }
      }

      const result = await dispatch(
        editVehicleImage({ vehicleId, imageId, body: { order: 1 } })
      );

      if (editVehicleImage.rejected.match(result)) {
        throw result.payload;
      }

      message.success(t('vehicle.messages.previewSetSuccess'));
    } catch (error) {
      message.error(error?.description || t('vehicle.messages.previewSetFail'));
    }
  };

  const deleteVehicleImageHandler = async (imageId) => {
    try {
      await dispatch(removeVehicleImage({ vehicleId, imageId }));
      message.success(t('vehicle.messages.imageDeleteSuccess'));
    } catch (error) {
      console.error('Failed to remove image:', error);
    }
  };

  const setTagsHandler = async (imageId, tags) => {
    try {
      const result = await dispatch(
        editVehicleImage({ vehicleId, imageId, body: { tags } })
      );

      if (editVehicleImage.rejected.match(result)) {
        throw result.payload;
      }

      dispatch(fetchVehicleImages({ vehicleId }));
      message.success('Successfully saved the selected tags!');
    } catch (error) {
      message.error(error?.description || t('vehicle.messages.tagsSaveFail'));
    }
  };

  const setImageTypeHandler = async (imageId, type) => {
    try {
      const result = await dispatch(
        editVehicleImage({ vehicleId, imageId, body: { type } })
      );

      if (editVehicleImage.rejected.match(result)) {
        throw result.payload;
      }

      await dispatch(fetchVehicleImages({ vehicleId }));
      message.success(t('vehicle.messages.typeSetSuccess', { type }));
    } catch (error) {
      message.error(error?.description || t('vehicle.messages.typeSetFail'));
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    const zip = new JSZip();
    const folder = zip.folder('images');

    for (const image of vehicleImages) {
      try {
        const imageUrl = image.path;
        const filename = image.name;

        const response = await fetch(imageUrl, {
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        const blob = await response.blob();
        folder.file(filename, blob);
      } catch (error) {
        message.error(
          t('vehicle.messages.imageFetchFail', { name: image.name })
        );
      }
    }

    zip
      .generateAsync({ type: 'blob' })
      .then((content) => {
        const fileName = `${vehicle.vehicleMake?.name}_${vehicle.vehicleModel?.name}_images.zip`;
        saveAs(content, fileName);

        message.success(t('vehicle.messages.zipSuccess'));
        setIsDownloading(false);
      })
      .catch((error) => {
        message.error(t('vehicle.messages.zipFail'));
        setIsDownloading(false);
      });
  };

  const handlePurchase = async () => {
    try {
      const userId = loggedInUser.id;

      await purchaseVehicle(userId, vehicleId);

      message.success(t('vehicle.messages.purchaseSuccess'));

      await Promise.all([
        dispatch(fetchVehicle({ vehicleId })),
        fetchBrandImages(enabledLogo, tempSymbolPhotoText, textSize),
        dispatch(fetchCarHouseById(loggedInUser.carHouseId)),
      ]);
    } catch (error) {
      const response = error.response.data;
      if (response.errorCode === 4030006) {
        message.error(t('vehicle.messages.noActiveSubscription'));
      } else if (response.errorCode === 4030005) {
        message.error(t('vehicle.messages.notEnoughCredits'));
      } else {
        message.error(
          response.description || t('vehicle.messages.purchaseFail')
        );
      }
    }
  };

  const fetchBrandImages = (
    logoState = enabledLogo,
    textState = symbolPhotoText,
    size = textSize // Add size parameter
  ) => {
    const params = {
      logoEnabled: logoState,
      ...(textState && { text: textState }),
      ...(size && { textSize: size }),
    };

    dispatch(
      fetchVehicleImagesBrands({
        vehicleId,
        options: { params },
      })
    ).catch((err) => console.warn('Brand info fetch failed:', err));
  };

  const checkIsVehiclePurchase = vehicleImages[0]?.vehicle?.carHouses?.some(
    (carHouse) => carHouse.id === loggedInUser.carHouseId
  );

  return (
    <>
      {isLoadingImages || isLoading ? (
        <Spinner />
      ) : (
        <Row
          style={{
            background: '#ffffff',
            boxShadow: 'rgb(178 213 198 / 56%) 2px 4px 8px 2px',
            borderRadius: '8px',
            padding: '40px 16px',
            margin: '0px',
          }}
          gutter={[32, 16]}
        >
          <Col xs={24} sm={24} md={14} lg={16}>
            <Row gutter={[16, 16]}>
              <Image.PreviewGroup>
                {vehicleImages.length ? (
                  vehicleImages.map((image) => (
                    <VehicleImageItem
                      key={image.id}
                      image={image}
                      deleteImage={deleteVehicleImageHandler}
                      setAsPreview={setAsPreviewImageHandler}
                      tags={tags}
                      setTags={setTagsHandler}
                      setImageType={setImageTypeHandler}
                    />
                  ))
                ) : (
                  <h1>{t('vehicle.messages.noImages')}</h1>
                )}
              </Image.PreviewGroup>
            </Row>
          </Col>
          <Col xs={24} sm={24} md={10} lg={8}>
            {(loggedInUser.role.name === 'user' ||
              loggedInUser.role.name === 'carHouseAdmin') && (
              <>
                <Row
                  justify="space-between"
                  align="middle"
                  style={{ marginBottom: 16 }}
                >
                  <Col>
                    <h3 style={{ margin: 0 }}>
                      {t('vehicle.imageSettings.title')}
                    </h3>
                  </Col>
                  <Col>
                    <Space>
                      {loggedInUser.logoId && (
                        <Space>
                          <span style={{ fontSize: '16px' }}>
                            {t('vehicle.imageSettings.logoLabel') || 'Logo:'}
                          </span>
                          <Switch
                            checked={enabledLogo}
                            onChange={(val) => {
                              setEnabledLogo(val);
                              fetchBrandImages(val, symbolPhotoText);
                            }}
                            checkedChildren={t('common.yes')}
                            unCheckedChildren={t('common.no')}
                          />
                        </Space>
                      )}

                      <Button
                        type="default"
                        shape="round"
                        onClick={() => {
                          setTempSymbolPhotoText(symbolPhotoText);
                          setIsModalVisible(true);
                        }}
                      >
                        {t('vehicle.imageSettings.symbolPhoto')}
                      </Button>
                    </Space>
                  </Col>
                </Row>
                <Divider />
              </>
            )}

            {(checkIsVehiclePurchase || loggedInUser.role.name === 'root') && (
              <Button
                type="primary"
                shape="round"
                ghost
                icon={<DownloadOutlined />}
                style={{ float: 'right', marginBottom: '15px' }}
                onClick={handleDownloadAll}
                loading={isDownloading}
                disabled={isDownloading}
              >
                {isDownloading
                  ? t('vehicle.actions.downloading')
                  : t('vehicle.actions.download')}
              </Button>
            )}
            {!checkIsVehiclePurchase &&
              (loggedInUser.role.name === 'user' ||
                loggedInUser.role.name === 'carHouseAdmin') && (
                <Button
                  type="primary"
                  shape="round"
                  style={{ float: 'right', marginBottom: '15px' }}
                  onClick={handlePurchase}
                >
                  {t('vehicle.actions.buy')}
                </Button>
              )}
            <VehicleInformation vehicle={vehicle} />
          </Col>
        </Row>
      )}

      <Modal
        title={t('vehicle.imageSettings.modal.title')}
        open={isModalVisible}
        onOk={() => {
          setSymbolPhotoText(tempSymbolPhotoText);
          setIsModalVisible(false);
          fetchBrandImages(enabledLogo, tempSymbolPhotoText);
        }}
        onCancel={() => setIsModalVisible(false)}
        okText={t('common.save')}
      >
        <Input
          placeholder={t('vehicle.imageSettings.modal.placeholder')}
          value={tempSymbolPhotoText}
          onChange={(e) => setTempSymbolPhotoText(e.target.value)}
          allowClear
        />
        <Divider />
        <Radio.Group
          value={textSize}
          onChange={(e) => setTextSize(e.target.value)}
        >
          <Radio value={8}>{t('vehicle.imageSettings.modal.small')}</Radio>
          <Radio value={18}>{t('vehicle.imageSettings.modal.medium')}</Radio>
          <Radio value={28}>{t('vehicle.imageSettings.modal.large')}</Radio>
        </Radio.Group>
      </Modal>
    </>
  );
};

export default Vehicle;
